import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import Button from '@components/ui-old/button';
import { useCart } from '@contexts/cart/cart.context';
import { useUI } from '@contexts/ui.context';
import { useProductsQuery } from '@framework/product/get-all-products';
import { Product } from '@framework/types';
import Counter from '@components/common/counter';
import { AiOutlineCheckCircle, AiOutlineCloseCircle } from 'react-icons/ai';
import { useTranslation } from 'next-i18next';
import { generateCartItem } from '@utils/generate-cart-item';
import { ToastProvider, Toast, ToastTitle, ToastClose, ToastViewport } from '@/components/ui/toast';
import { CircleCheckIcon, XIcon } from 'lucide-react';
import { useProgressTimer } from '@/hooks/use-progress-timer';
import { getCurrentLocationAndLanguage } from '@utils/location';
Modal.setAppElement('#__next');
interface Item {
  code: string;
  quantity: number;
  product: Product | null;
}
const initialItems: Item[] = [{
  code: '',
  quantity: 1,
  product: null
}, {
  code: '',
  quantity: 1,
  product: null
}, {
  code: '',
  quantity: 1,
  product: null
}];
export default function QuickOrderModal() {
  const {
    t
  } = useTranslation('common');
  const {
    location
  } = getCurrentLocationAndLanguage();
  const {
    addItemToCart
  } = useCart();
  const {
    openCart,
    isAuthorized,
    setModalView,
    openModal: openLoginModal
  } = useUI();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [shouldOpenModalAfterLogin, setShouldOpenModalAfterLogin] = useState<boolean>(false);
  const [items, setItems] = useState<Item[]>(initialItems);
  const [addToCartLoader, setAddToCartLoader] = useState<boolean>(false);
  const [, setViewCartBtn] = useState<boolean>(false);
  const {
    data,
    fetchNextPage,
    hasNextPage
  } = useProductsQuery({
    limit: 999999
  });
  const allProducts = data ? data.pages.flatMap(page => page.results) : [];
  useEffect(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  // Toast state for notifications
  const [toastOpen, setToastOpen] = useState(false);
  const toastDuration = 3000;
  const {
    progress,
    start,
    pause,
    resume
  } = useProgressTimer({
    duration: toastDuration,
    onComplete: () => setToastOpen(false)
  });
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const openQuickOrderModal = () => {
    if (location !== 'kg' && !isAuthorized) {
      setModalView('LOGIN_VIEW');
      openLoginModal();
      setShouldOpenModalAfterLogin(true);
      return;
    }
    setItems(initialItems);
    setIsOpen(true);
  };
  const closeModal = () => {
    setIsOpen(false);
    setItems(initialItems);
  };
  const addNewItem = () => {
    setItems([...items, {
      code: '',
      quantity: 1,
      product: null
    }]);
  };
  const handleInputChange = (index: number, field: keyof Item, value: any) => {
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      [field]: value
    };
    if (field === 'code') {
      const product = allProducts.find(p => p.productCode.toLowerCase() === value.toLowerCase());
      newItems[index].product = product || null;
    }
    setItems(newItems);
  };
  const addToCart = () => {
    if (location !== 'kg' && !isAuthorized) {
      setModalView('LOGIN_VIEW');
      openLoginModal();
      return;
    }
    const validItems = items.filter(item => item.code && item.product && item.product.amount > 0);
    if (!validItems.length) {
      setToastType('error');
      setToastMessage(t('text-add-product-to-purchase'));
      setToastOpen(true);
      start();
      return;
    }
    setAddToCartLoader(true);
    setTimeout(() => {
      setAddToCartLoader(false);
      validItems.forEach(item => {
        if (item.product) {
          if (item.product.amount === 0) {
            return;
          }
          const cartItem = generateCartItem(item.product, {});
          addItemToCart(cartItem, item.quantity);
        }
      });
      setItems(initialItems);
      closeModal();
      setViewCartBtn(true);
      openCart();
      setToastType('success');
      setToastMessage(t('text-added-to-cart'));
      setToastOpen(true);
      start();
    }, 600);
  };
  useEffect(() => {
    if (location !== 'kg' && isAuthorized && shouldOpenModalAfterLogin) {
      setIsOpen(true);
      setShouldOpenModalAfterLogin(false);
    }
  }, [isAuthorized, shouldOpenModalAfterLogin]);
  return <ToastProvider swipeDirection="right" data-sentry-element="ToastProvider" data-sentry-component="QuickOrderModal" data-sentry-source-file="express-shopping.tsx">
      <>
        <button onClick={openQuickOrderModal} className="btn-primary z-10 bg-aloeGold text-black border border-yellow-500 w-full min-w-[200px] h-12 rounded-lg hover:bg-yellow-600 hover:border-yellow-600 transition-colors duration-300">
          {t('express-shoping')}
        </button>
        <Modal isOpen={isOpen} onRequestClose={closeModal} contentLabel="Express Shopping Modal" className="flex items-center justify-center" overlayClassName="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center" data-sentry-element="Modal" data-sentry-source-file="express-shopping.tsx">
          <div className="bg-white dark:bg-neutral-900 rounded-lg w-full max-w-md p-5 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold mb-4">{t('express-shoping')}</h3>
              <button onClick={closeModal} className="text-gray-600 dark:text-neutral-300 text-2xl hover:text-gray-900 dark:hover:text-white mb-4">
                &times;
              </button>
            </div>
            <div className="grid grid-cols-3 gap-4 mb-4 items-center">
              <span className="font-semibold text-center dark:text-neutral-100">{t('text-product-code')}</span>
              <span className="font-semibold text-center dark:text-neutral-100">{t('text-quantity')}</span>
              <span className="font-semibold text-center dark:text-neutral-100">{t('text-product-status')}</span>
            </div>

            {items.map((item, index) => <div key={index} className="grid grid-cols-3 gap-4 mb-4 items-center">
                <input type="text" placeholder="###" value={item.code} onChange={e => handleInputChange(index, 'code', e.target.value)} className="border p-2 text-center w-full dark:bg-neutral-800 dark:text-white dark:border-neutral-700" />
                <div className="flex items-center justify-center">
                  <Counter quantity={item.quantity} onIncrement={() => handleInputChange(index, 'quantity', item.quantity + 1)} onDecrement={() => handleInputChange(index, 'quantity', Math.max(1, item.quantity - 1))} disableDecrement={item.quantity === 1} disableIncrement={item.quantity >= 30 || item.product?.amount !== undefined && item.quantity >= item.product.amount} variant="light" />
                </div>

                <div className="flex justify-center">
                  {item.code && item.product && item.product.amount > 0 ? <AiOutlineCheckCircle className="text-green-500 text-2xl" /> : item.code ? <AiOutlineCloseCircle className="text-red-500 text-2xl" /> : null}
                </div>
              </div>)}
            <Button onClick={addNewItem} className="w-full h-12 mb-4 bg-neutral-600 text-white dark:bg-neutral-700 mt-4" data-sentry-element="Button" data-sentry-source-file="express-shopping.tsx">
              {t('text-add-new-product')}
            </Button>
            <Button onClick={addToCart} loading={addToCartLoader} className="w-full h-12 bg-black text-white" data-sentry-element="Button" data-sentry-source-file="express-shopping.tsx">
              {t('text-purchase')}
            </Button>
          </div>
        </Modal>
      </>
      <Toast open={toastOpen} onOpenChange={open => {
      setToastOpen(open);
      if (open) start();
    }} onPause={pause} onResume={resume} data-sentry-element="Toast" data-sentry-source-file="express-shopping.tsx">
        <div className="flex w-full justify-between gap-3">
          {toastType === 'success' ? <CircleCheckIcon size={16} className="mt-0.5 text-emerald-500" /> : <XIcon size={16} className="mt-0.5 text-red-500" />}
          <div className="flex flex-col flex-grow gap-3">
            <ToastTitle data-sentry-element="ToastTitle" data-sentry-source-file="express-shopping.tsx">{toastMessage}</ToastTitle>
          </div>
          <ToastClose data-sentry-element="ToastClose" data-sentry-source-file="express-shopping.tsx">
            <button className="p-1">
              <XIcon size={16} data-sentry-element="XIcon" data-sentry-source-file="express-shopping.tsx" />
            </button>
          </ToastClose>
        </div>
        <div className={`absolute -left-2 right-0 bottom-0 h-1 ${toastType === 'success' ? 'bg-emerald-500' : 'bg-red-500'}`} style={{
        width: `${progress / toastDuration * 100}%`,
        transition: 'width 100ms linear'
      }} />
      </Toast>
      <ToastViewport className="fixed top-4 right-4 z-[9999] sm:bottom-4 sm:top-auto" data-sentry-element="ToastViewport" data-sentry-source-file="express-shopping.tsx" />
    </ToastProvider>;
}